import React from 'react';

// Canonical production origin for the marketing site. Used to build absolute
// canonical + Open Graph URLs. Update here if the domain ever changes.
export const SITE_URL = 'https://qentoros.com';
export const SITE_NAME = 'Qentoros';

/**
 * Per-page SEO metadata.
 *
 * React 19 hoists <title>, <meta> and <link> rendered anywhere in the tree into
 * <head>, so this component just returns them as a fragment — no provider or
 * external head-management library needed. It owns the tags that engines which
 * execute JavaScript (e.g. Googlebot) use per route: title, description,
 * canonical, and optional JSON-LD.
 *
 * Open Graph / Twitter card tags are intentionally NOT emitted here. Social
 * scrapers (LinkedIn, Slack, iMessage, Facebook) do not run JS, so they only
 * ever read the static tags in index.html. Duplicating per-page OG here would
 * add tags no scraper consumes while doubling them in the rendered DOM. The
 * single brand-level OG card lives in index.html; true per-page social cards
 * would require prerendering/SSR.
 *
 * Props:
 *  - title:       full <title> (already brand-suffixed by the caller)
 *  - description: meta description
 *  - path:        absolute path from the site root, e.g. '/products/bio-ply'
 *  - noindex:     when true, ask engines not to index (private/app routes)
 *  - jsonLd:      optional schema.org object rendered as JSON-LD
 */
export default function Seo({
  title,
  description,
  path = '/',
  noindex = false,
  jsonLd,
}) {
  const url = `${SITE_URL}${path}`;

  return (
    <>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <link rel="canonical" href={url} />
      )}

      {jsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </>
  );
}
